"use client"

import type React from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import {
  Home,
  Activity,
  Package,
  Store,
  Ticket,
  Swords,
  Users2,
  HeartPulse,
  Send,
  Settings,
  ShieldCheck,
  Building2,
  UserCog,
  PanelLeft,
  X,
} from "lucide-react"

import { cn } from "@/lib/utils"
import { apiUrl, authHeaders, timeAgo } from "@/lib/api"
import { useTenant } from "@/contexts/tenant-context"
import { useAuth } from "@/contexts/auth-context"
import { useSidebar } from "@/contexts/sidebar-context"
import Sparkline from "@/components/dashboard-cms/sparkline"
import MobileInstallCard from "@/components/dashboard-cms/mobile-install-card"

interface MenuItem {
  id: string
  label: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  badge?: string
  isNew?: boolean
  requiredFeature?: string
}

interface MenuSection {
  id: string
  label: string
  items: MenuItem[]
}

const menuData: MenuSection[] = [
  {
    id: "operation",
    label: "Operação",
    items: [
      { id: "overview", label: "Visão geral", href: "/dashboard-cms", icon: Home },
      {
        id: "realtime",
        label: "Tempo real",
        href: "/dashboard-cms/realtime",
        icon: Activity,
        isNew: true,
        requiredFeature: "realtime",
      },
      { id: "products", label: "Produtos", href: "/dashboard-cms/produtos", icon: Package },
      { id: "stores", label: "Lojas", href: "/dashboard-cms/lojas", icon: Store },
      { id: "coupons", label: "Cupons", href: "/dashboard-cms/cupons", icon: Ticket },
    ],
  },
  {
    // Telegram agrupa: configuração do bot + monitor de concorrentes (Spy).
    // Antes, Spy ficava em "Operação" e Telegram em "Sistema" — divorciados.
    // Tudo do Telegram (bot, destinos, Telethon, canais espionados) num lugar só.
    id: "telegram",
    label: "Telegram",
    items: [
      {
        id: "telegram",
        label: "Configuração",
        href: "/dashboard-cms/ferramentas/telegram",
        icon: Send,
      },
      {
        id: "spy",
        label: "Concorrentes",
        href: "/dashboard-cms/spy",
        icon: Swords,
        isNew: true,
        requiredFeature: "spy",
      },
    ],
  },
  {
    id: "system",
    label: "Sistema",
    items: [
      {
        id: "health",
        label: "Saúde",
        href: "/dashboard-cms/saude",
        icon: HeartPulse,
        requiredFeature: "saude",
      },
    ],
  },
  {
    id: "team",
    label: "Equipe",
    items: [
      {
        id: "members",
        label: "Membros",
        href: "/dashboard-cms/equipe/membros",
        icon: Users2,
        requiredFeature: "membros",
      },
    ],
  },
]

const adminSection: MenuSection = {
  id: "platform",
  label: "Plataforma",
  items: [
    { id: "admin-overview", label: "Painel admin", href: "/admin", icon: ShieldCheck },
    { id: "admin-tenants", label: "Tenants", href: "/admin/tenants", icon: Building2 },
    { id: "admin-users", label: "Usuários", href: "/admin/users", icon: UserCog },
  ],
}

type HealthStatus = "operational" | "slow" | "stalled" | "unknown"

interface SystemPulse {
  activeCoupons: number
  lastCapturedAt: string | null
  status: HealthStatus
  sparkData: number[]
}

const SPARK_BUCKETS = 24 // last 24h in 1h buckets

function bucketLast24h(timestamps: string[]): number[] {
  const buckets = new Array(SPARK_BUCKETS).fill(0)
  const now = Date.now()
  const hourMs = 60 * 60 * 1000
  for (const ts of timestamps) {
    if (!ts) continue
    const diff = now - new Date(ts).getTime()
    if (diff < 0 || diff > SPARK_BUCKETS * hourMs) continue
    const bucket = SPARK_BUCKETS - 1 - Math.floor(diff / hourMs)
    if (bucket >= 0 && bucket < SPARK_BUCKETS) buckets[bucket] += 1
  }
  return buckets
}

function resolveHealth(lastCapturedAt: string | null): HealthStatus {
  if (!lastCapturedAt) return "unknown"
  const diffMin = (Date.now() - new Date(lastCapturedAt).getTime()) / 60000
  if (diffMin < 30) return "operational"
  if (diffMin < 180) return "slow"
  return "stalled"
}

function isActivePath(pathname: string | null, href: string): boolean {
  if (!pathname) return false
  if (href === "/dashboard-cms") return pathname === "/dashboard-cms"
  if (href === "/admin") return pathname === "/admin"
  return pathname === href || pathname.startsWith(`${href}/`)
}

const healthDotStyle: Record<HealthStatus, { bg: string; ring: string; pulse: boolean; label: string }> = {
  operational: {
    bg: "bg-emerald-500",
    ring: "ring-emerald-500/30",
    pulse: true,
    label: "Sistema operacional",
  },
  slow: {
    bg: "bg-[var(--brand-amber)]",
    ring: "ring-[var(--brand-amber)]/25",
    pulse: false,
    label: "Captura lenta",
  },
  stalled: {
    bg: "bg-rose-500",
    ring: "ring-rose-500/25",
    pulse: false,
    label: "Sem captura recente",
  },
  unknown: {
    bg: "bg-muted-foreground/60",
    ring: "ring-muted-foreground/20",
    pulse: false,
    label: "Sem dados",
  },
}

// Smooth count-up tween, ease-out-expo. First non-null target lands instantly;
// subsequent changes animate so polling refreshes don't constantly redraw from 0.
function useCountUp(target: number, duration = 600): number {
  const [value, setValue] = useState(target)
  const previousRef = useRef(target)
  const seededRef = useRef(false)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!seededRef.current) {
      seededRef.current = true
      previousRef.current = target
      setValue(target)
      return
    }
    const from = previousRef.current
    const to = target
    if (from === to) return

    const start = performance.now()
    const tick = (now: number) => {
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(2, -10 * progress)
      const next = from + (to - from) * eased
      setValue(progress >= 1 ? to : next)
      if (progress < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    previousRef.current = to

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [target, duration])

  return Math.round(value)
}

const EASTER_EGG_KEY = "promobot_sidebar_signature_v1"

function fireConsoleSignature(brandName: string) {
  if (typeof window === "undefined") return
  try {
    if (window.sessionStorage.getItem(EASTER_EGG_KEY)) return
    window.sessionStorage.setItem(EASTER_EGG_KEY, "1")
  } catch {
    return
  }
  const title = `%c${brandName.toLowerCase()}`
  const subtitle = `%c· dashboard editorial · feito à mão por humanos teimosos`
  console.log(
    title + " " + subtitle,
    "color: #c8821a; font: italic 22px 'Instrument Serif', Georgia, serif;",
    "color: #888; font: 11px ui-monospace, 'JetBrains Mono', monospace;",
  )
}

export default function Sidebar() {
  const pathname = usePathname()
  const { tenant, hasFeature } = useTenant()
  const { user } = useAuth()
  const {
    menuState,
    isHovered,
    isMobile,
    isMobileOpen,
    lastToggleAt,
    setIsHovered,
    setIsMobileOpen,
    toggleMenuState,
  } = useSidebar()

  const [pulse, setPulse] = useState<SystemPulse>({
    activeCoupons: 0,
    lastCapturedAt: null,
    status: "unknown",
    sparkData: [],
  })
  const [logoHovered, setLogoHovered] = useState(false)
  const logoOpenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const logoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Animated count-up for active coupons
  const displayedCount = useCountUp(pulse.activeCoupons)

  // Console easter egg on mount
  useEffect(() => {
    fireConsoleSignature(tenant?.name || "ofertasbot")
  }, [tenant?.name])

  // Hover handlers with debounce — prevents popover flicker when cursor grazes the logo
  function openLogoPopover() {
    if (logoCloseTimerRef.current) {
      clearTimeout(logoCloseTimerRef.current)
      logoCloseTimerRef.current = null
    }
    if (logoHovered) return
    logoOpenTimerRef.current = setTimeout(() => setLogoHovered(true), 180)
  }
  function closeLogoPopover() {
    if (logoOpenTimerRef.current) {
      clearTimeout(logoOpenTimerRef.current)
      logoOpenTimerRef.current = null
    }
    logoCloseTimerRef.current = setTimeout(() => setLogoHovered(false), 140)
  }
  useEffect(() => {
    return () => {
      if (logoOpenTimerRef.current) clearTimeout(logoOpenTimerRef.current)
      if (logoCloseTimerRef.current) clearTimeout(logoCloseTimerRef.current)
    }
  }, [])

  // Data fetch — single endpoint gives us count, last captured, and 24h spark
  useEffect(() => {
    let cancelled = false

    async function fetchPulse() {
      const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
      if (!token) return

      try {
        const [latestRes, activeRes] = await Promise.all([
          fetch(`${apiUrl()}/coupons/?ordering=-created_at&page_size=100`, { headers: authHeaders(token) }),
          fetch(`${apiUrl()}/coupons/?status=ativo&page_size=1`, { headers: authHeaders(token) }),
        ])

        const latestData = latestRes.ok ? await latestRes.json() : null
        const activeData = activeRes.ok ? await activeRes.json() : null

        if (cancelled) return

        const latestList: Array<{ created_at?: string }> =
          latestData?.results || (Array.isArray(latestData) ? latestData : [])
        const lastCapturedAt: string | null = latestList[0]?.created_at || null
        const timestamps = latestList.map((c) => c.created_at).filter((t): t is string => Boolean(t))

        setPulse({
          activeCoupons: typeof activeData?.count === "number" ? activeData.count : latestList.length,
          lastCapturedAt,
          status: resolveHealth(lastCapturedAt),
          sparkData: bucketLast24h(timestamps),
        })
      } catch {
        if (!cancelled) setPulse((prev) => ({ ...prev, status: "unknown" }))
      }
    }

    fetchPulse()
    const interval = setInterval(fetchPulse, 60_000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [user?.id])

  const showText = menuState === "full" || (menuState === "collapsed" && isHovered) || (isMobile && isMobileOpen)
  const collapsedOnly = menuState === "collapsed" && !isHovered && !isMobile

  const filteredMenuData = useMemo(() => {
    const sections: MenuSection[] = menuData
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.requiredFeature || hasFeature(item.requiredFeature)),
      }))
      .filter((section) => section.items.length > 0)

    if (user?.is_superuser) sections.push(adminSection)
    return sections
  }, [hasFeature, user?.is_superuser])

  const numberedSections = useMemo(() => {
    let counter = 0
    return filteredMenuData.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        counter += 1
        return { ...item, ordinal: counter }
      }),
    }))
  }, [filteredMenuData])

  function handleNavClick() {
    if (isMobile) setIsMobileOpen(false)
  }

  const sidebarWidth = (() => {
    if (isMobile) return "w-64"
    if (menuState === "collapsed" && isHovered) return "w-64"
    return menuState === "collapsed" ? "w-[64px]" : "w-64"
  })()

  const brandName = tenant?.name || "Dashboard"
  const brandLogo = tenant?.logo_url || "/logo.png"
  const healthStyle = healthDotStyle[pulse.status]

  const scrapersLine = (() => {
    switch (pulse.status) {
      case "operational":
        return "Captura ativa nos últimos 30 min."
      case "slow":
        return "Captura mais lenta, aguardando janela."
      case "stalled":
        return "Sem captura há mais de 3h."
      default:
        return "Sem dados ainda."
    }
  })()

  const Header = () => (
    <div className="h-16 flex items-center gap-2 px-3 border-b border-panel-border relative">
      {showText ? (
        <div
          className="relative flex-1 min-w-0"
          onMouseEnter={openLogoPopover}
          onMouseLeave={closeLogoPopover}
          onFocus={openLogoPopover}
          onBlur={closeLogoPopover}
        >
          <Link
            href="/dashboard-cms"
            className="flex items-center gap-2.5 w-full min-w-0 group"
            aria-label={`${brandName} home`}
          >
            <img
              src={brandLogo}
              alt=""
              width={28}
              height={28}
              className="flex-shrink-0 rounded-md object-contain"
              aria-hidden="true"
            />
            <span className="font-display italic text-[19px] leading-none tracking-[-0.01em] text-foreground truncate">
              {brandName}
            </span>
            {/* Em mobile, o close button toma esse espaço (renderizado fora
                do <Link> abaixo) — health dot some na navegação ativa. */}
            {!isMobile && (
              <span
                className={cn(
                  "relative ml-auto flex-shrink-0 h-2 w-2 rounded-full ring-2 transition-colors",
                  healthStyle.bg,
                  healthStyle.ring,
                )}
                title={healthStyle.label}
                aria-label={healthStyle.label}
              >
                {healthStyle.pulse && (
                  <span
                    className={cn("absolute inset-0 rounded-full animate-ping opacity-60", healthStyle.bg)}
                    aria-hidden="true"
                  />
                )}
              </span>
            )}
          </Link>

          {logoHovered && (
            <div
              role="tooltip"
              className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 mx-1 rounded-lg border border-panel-border bg-panel shadow-[0_12px_32px_-12px_rgba(0,0,0,0.18)] p-3.5 animate-fade-in"
              style={{ animationDuration: "180ms" }}
            >
              <div className="flex items-center gap-2 text-xs">
                <span
                  className={cn("h-1.5 w-1.5 rounded-full", healthStyle.bg)}
                  aria-hidden="true"
                />
                <span className="font-medium text-foreground">{healthStyle.label}</span>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground leading-relaxed">
                {scrapersLine}
              </p>
              <div className="mt-2.5 pt-2.5 border-t border-panel-border/70 grid grid-cols-2 gap-2 text-[11px]">
                <div>
                  <div className="text-muted-foreground/70 uppercase tracking-[0.14em] text-[10px]">
                    Ativos
                  </div>
                  <div className="font-mono tabular-nums text-foreground mt-0.5">
                    {pulse.activeCoupons}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground/70 uppercase tracking-[0.14em] text-[10px]">
                    Última captura
                  </div>
                  <div className="font-mono tabular-nums text-foreground mt-0.5">
                    {pulse.lastCapturedAt ? timeAgo(pulse.lastCapturedAt) : "—"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex w-full justify-center relative" title={`${brandName} · ${healthStyle.label}`}>
          <img src={brandLogo} alt={brandName} width={28} height={28} className="rounded-md object-contain" />
          <span
            className={cn(
              "absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-background",
              healthStyle.bg,
            )}
            aria-label={healthStyle.label}
          />
        </div>
      )}
      {/* Close button mobile — flex sibling, não absolute. Sempre fica
          encostado na direita do Header sem sobrepor logo/nome. */}
      {isMobile && (
        <button
          type="button"
          onClick={() => setIsMobileOpen(false)}
          aria-label="Fechar menu"
          className="tap-44 flex-shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  )

  const NavItemRow = ({ item }: { item: MenuItem & { ordinal?: number } }) => {
    const Icon = item.icon
    const isActive = isActivePath(pathname, item.href)
    const ordinalLabel = item.ordinal ? String(item.ordinal).padStart(2, "0") : ""

    return (
      <Link
        href={item.href}
        onClick={handleNavClick}
        aria-current={isActive ? "page" : undefined}
        className={cn(
          "group relative flex items-center rounded-md transition-colors text-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40 focus-visible:ring-offset-1 focus-visible:ring-offset-background",
          collapsedOnly ? "h-9 mx-1 justify-center" : "h-9 mx-1 px-2.5 gap-2.5",
          isActive
            ? "bg-muted/60 text-foreground"
            : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
        )}
      >
        {isActive && (
          <span
            aria-hidden="true"
            className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r-sm bg-[var(--brand-amber)]"
          />
        )}

        {showText ? (
          <>
            <span
              className={cn(
                "font-mono text-[10px] tabular-nums w-5 flex-shrink-0 transition-colors",
                isActive ? "text-[var(--brand-amber)]" : "text-muted-foreground/55 group-hover:text-muted-foreground",
              )}
              aria-hidden="true"
            >
              {ordinalLabel}
            </span>
            <Icon
              className={cn(
                "h-[15px] w-[15px] flex-shrink-0 transition-colors",
                isActive ? "text-[var(--brand-amber)]" : "text-muted-foreground/80 group-hover:text-foreground",
              )}
            />
            <span className={cn("flex-1 truncate", isActive && "font-medium")}>{item.label}</span>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {item.isNew && (
                <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--brand-amber)] bg-[var(--brand-amber)]/12 px-1.5 py-0.5 rounded leading-none">
                  novo
                </span>
              )}
              {item.badge && (
                <span className="font-mono text-[11px] tabular-nums text-muted-foreground">{item.badge}</span>
              )}
            </div>
          </>
        ) : (
          <>
            <Icon
              className={cn(
                "h-[17px] w-[17px] transition-colors",
                isActive ? "text-[var(--brand-amber)]" : "text-muted-foreground/85 group-hover:text-foreground",
              )}
            />
            {item.isNew && (
              <span
                className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-[var(--brand-amber)]"
                aria-hidden="true"
              />
            )}
            <span
              role="tooltip"
              className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50"
            >
              {item.label}
            </span>
          </>
        )}
      </Link>
    )
  }

  const NavContent = () => (
    <div
      className="flex-1 overflow-y-auto overflow-x-hidden py-4 scrollbar-none"
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      <div className={showText ? "space-y-6" : "space-y-3"}>
        {numberedSections.map((section, idx) => (
          <div key={section.id}>
            {showText ? (
              <div className="px-3.5 mb-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">
                {section.label}
              </div>
            ) : idx > 0 ? (
              <div className="mx-3 my-2 h-px bg-panel-border/80" aria-hidden="true" />
            ) : null}
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavItemRow key={item.id} item={item} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const Footer = () => {
    const hasSpark = pulse.sparkData.some((v) => v > 0)

    return (
      <div className="px-3 pb-3 pt-2 border-t border-panel-border/70">
        <div className={cn(showText ? "-mx-1 mb-2" : "mb-2")}>
          {showText ? (
            <MobileInstallCard variant="sidebar" className="mx-0 mt-0" />
          ) : (
            <MobileInstallCard variant="sidebar-skinny" className="mx-0 my-0" />
          )}
        </div>
        {showText && (
          <div className="pb-3">
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="text-muted-foreground/80">Cupons hoje</span>
              <span className="font-mono tabular-nums text-foreground">
                {pulse.lastCapturedAt ? displayedCount : "—"}
              </span>
            </div>
            {hasSpark && (
              <div className="mt-1.5 -mx-0.5" aria-hidden="true">
                <Sparkline
                  data={pulse.sparkData}
                  width="100%"
                  height={20}
                  color="var(--brand-amber)"
                  showFill
                  strokeWidth={1.25}
                  animateDraw
                />
              </div>
            )}
            <div className="mt-2 flex items-baseline justify-between gap-2 text-xs">
              <span className="text-muted-foreground/80">Última captura</span>
              <span className="font-mono tabular-nums text-muted-foreground">
                {pulse.lastCapturedAt ? timeAgo(pulse.lastCapturedAt) : "—"}
              </span>
            </div>
          </div>
        )}

        <Link
          href="/settings"
          onClick={handleNavClick}
          className={cn(
            "group flex items-center rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40 focus-visible:ring-offset-1 focus-visible:ring-offset-background",
            collapsedOnly ? "h-9 justify-center" : "h-9 px-2.5 gap-2.5",
          )}
        >
          <Settings className="h-[15px] w-[15px] flex-shrink-0 text-muted-foreground/80 group-hover:text-foreground transition-colors" />
          {showText && <span className="flex-1">Configurações</span>}
        </Link>

        {showText && !isMobile && (
          <button
            type="button"
            onClick={toggleMenuState}
            className="mt-1 flex w-full items-center gap-2.5 h-8 px-2.5 rounded-md text-xs text-muted-foreground/70 hover:text-foreground hover:bg-muted/40 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40 focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            aria-label="Alternar estado da sidebar"
          >
            <PanelLeft className="h-[13px] w-[13px]" />
            <span className="flex-1 text-left">Recolher</span>
            <kbd
              key={lastToggleAt ?? "init"}
              className={cn(
                "font-mono text-[10px] tracking-wider text-muted-foreground/50 px-1 py-0.5 rounded",
                lastToggleAt && "animate-kbd-flash",
              )}
            >
              ⌘\
            </kbd>
          </button>
        )}
      </div>
    )
  }

  if (isMobile) {
    return (
      <>
        <nav
          aria-label="Navegação principal"
          className={cn(
            "fixed top-3 bottom-3 left-3 z-[70] w-[min(17rem,calc(100vw-1.5rem))] bg-panel border border-panel-border rounded-2xl shadow-float overflow-hidden",
            "transform transition-transform duration-300 ease-out",
            isMobileOpen ? "translate-x-0" : "-translate-x-[calc(100%+0.75rem)]",
          )}
        >
          {/* Close button agora vive DENTRO do Header — ver const Header acima.
              O X anterior flutuava em -right-2 cortando a chrome da sidebar
              e em viewports estreitas saía da viewport visível. */}
          <div className="h-full flex flex-col">
            <Header />
            <NavContent />
            <Footer />
          </div>
        </nav>
        {isMobileOpen && (
          <div
            className="fixed inset-0 bg-foreground/40 backdrop-blur-[2px] z-[65]"
            onClick={() => setIsMobileOpen(false)}
            aria-hidden="true"
          />
        )}
      </>
    )
  }

  return (
    <nav
      aria-label="Navegação principal"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        "fixed top-3 bottom-3 left-3 z-[60] bg-panel border border-panel-border rounded-2xl shadow-float transition-[width,opacity,box-shadow] duration-300 ease-out",
        menuState === "hidden" && "w-0 border-0 shadow-none opacity-0 pointer-events-none",
        menuState !== "hidden" && sidebarWidth,
      )}
      style={{ overflow: menuState === "hidden" ? "hidden" : "visible" }}
    >
      {menuState !== "hidden" && (
        <div className="h-full flex flex-col">
          <Header />
          <NavContent />
          <Footer />
        </div>
      )}
    </nav>
  )
}
